import { useAuth } from '@/components/providers/auth-provider';
import { useUiStore } from '@/stores/ui-store';
import { t } from '@/lib/i18n';

/**
 * Status section content for the AppShell right rail.
 *
 * Mirrors the classification + clearance + session block that previously
 * lived in the sidebar footer. Lives in the rail now so it's contextual
 * with the page (still visible, but no longer competing for sidebar space).
 */
export function StatusPaneSection() {
  const { user, activeOrg } = useAuth();
  const lang = useUiStore((s) => s.lang);
  const orgRoleLabel = activeOrg?.role ?? '—';

  return (
    <div className="space-y-4 p-5 text-sm">
      <div className="rounded-sm border border-[var(--color-border)] p-3 font-mono text-xs text-[var(--color-ink-muted)]">
        <div className="flex items-center justify-between">
          <span>{t('pane.status.classification', lang)}</span>
          <span className="text-[var(--color-accent-bright)]">
            {t('pane.status.classification.value', lang)}
          </span>
        </div>
        <div className="mt-1 flex items-center justify-between">
          <span>{t('pane.status.clearance', lang)}</span>
          <span className="uppercase text-[var(--color-ink)]">
            {orgRoleLabel}
          </span>
        </div>
        <div className="mt-1 flex items-center justify-between">
          <span>{t('pane.status.session', lang)}</span>
          <span className="flex items-center gap-1 text-[var(--color-success)]">
            <span
              className="inline-block size-1.5 rounded-pill pulse-ring"
              style={{ background: 'var(--color-success)' }}
              aria-hidden
            />
            {t('pane.status.session.live', lang)}
          </span>
        </div>
      </div>

      {user && (
        <div>
          <div className="mb-1 text-xxs uppercase tracking-wider text-[var(--color-ink-muted)]">
            {t('pane.status.signed-in', lang)}
          </div>
          <div className="break-all font-mono text-xs text-[var(--color-ink)]">
            {user.email}
          </div>
          <div className="mt-1 inline-block rounded-xs bg-[var(--color-accent-soft)] px-1.5 py-0.5 font-mono text-xxs uppercase tracking-wider text-[var(--color-accent)]">
            {orgRoleLabel}
          </div>
        </div>
      )}

      {activeOrg && (
        <div>
          <div className="mb-1 text-xxs uppercase tracking-wider text-[var(--color-ink-muted)]">
            {t('pane.status.org', lang)}
          </div>
          <div className="text-sm text-[var(--color-ink)]">
            {activeOrg.name}
          </div>
          <div className="font-mono text-xxs text-[var(--color-ink-muted)]">
            {activeOrg.id}
          </div>
        </div>
      )}
    </div>
  );
}
